import { AddOutlined, CloseOutlined, SendToMobileOutlined } from "@mui/icons-material";
import { Alert, Autocomplete, Box, Button, IconButton, Paper, TextField, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import GPositionsMap from "../../components/example/GPositionMap";
import { GTR_MAPS_KEY } from "../../consts/runtimeConfig";
import { setToastRefToOpen } from "../../functions/functions";
import findAllPlacesWithIqLocation from "../../resources/Api/location/findAllPlacesWithIqLocation";
import Directions from "./reports/components/Directions";

type Place = { display_name: string; lat: string | number; lon: string | number };
type Point = { query: string; options: Place[]; lat: number; lng: number };
const emptyPoint = (): Point => ({ query: "", options: [], lat: 0, lng: 0 });
const MAX_POINTS = 10;
const routeFieldSx = {
    "& .MuiOutlinedInput-root": {
        minHeight: 56,
        borderRadius: 2,
        backgroundColor: "background.paper",
        "& fieldset": { borderColor: "rgba(0, 63, 74, 0.24)" },
        "&:hover fieldset": { borderColor: "#006C77" },
        "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0, 183, 0, 0.12)" },
        "&.Mui-focused fieldset": { borderColor: "#00B700", borderWidth: 2 },
    },
    "& .MuiInputLabel-root": { fontFamily: "Prometo" },
};

const RoutesSimpleTab = () => {
    const mapRef = useRef<any>(null);
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const requestNumber = useRef(0);
    const toastRef = useRef<Toast>(null);
    const [points, setPoints] = useState<Point[]>([emptyPoint(), emptyPoint()]);
    const [loadingIndex, setLoadingIndex] = useState<number | null>(null);

    useEffect(() => () => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        requestNumber.current += 1;
    }, []);

    const updatePoint = useCallback((index: number, patch: Partial<Point>) => {
        setPoints((current) => current.map((point, position) => position === index ? { ...point, ...patch } : point));
    }, []);

    const searchAddress = useCallback((index: number, query: string) => {
        updatePoint(index, { query, lat: 0, lng: 0, options: [] });
        if (searchTimer.current) clearTimeout(searchTimer.current);
        const currentRequest = ++requestNumber.current;
        if (query.trim().length < 3) return;
        searchTimer.current = setTimeout(async () => {
            setLoadingIndex(index);
            try {
                const response = await findAllPlacesWithIqLocation(query.trim());
                if (currentRequest === requestNumber.current) {
                    updatePoint(index, { options: Array.isArray(response.data) ? response.data : [] });
                }
            } catch {
                if (currentRequest === requestNumber.current) setToastRefToOpen(toastRef, "Não foi possível pesquisar o endereço.", "Atenção!", "error", undefined, 3000, false);
            } finally {
                if (currentRequest === requestNumber.current) setLoadingIndex(null);
            }
        }, 500);
    }, [updatePoint]);

    const selectPlace = (index: number, value: Place) => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        requestNumber.current += 1;
        setLoadingIndex(null);
        updatePoint(index, { query: value.display_name, lat: Number(value.lat), lng: Number(value.lon), options: [] });
    };

    const positions = useMemo(() => points.every((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng) && (point.lat !== 0 || point.lng !== 0))
        ? points.map(({ lat, lng }) => ({ lat, lng })) : [], [points]);

    const onRouteChanged = useCallback((updated: { lat: number; lng: number }[]) => {
        setPoints((current) => {
            if (updated.length !== current.length || current.every((point, index) => point.lat === updated[index].lat && point.lng === updated[index].lng)) return current;
            return current.map((point, index) => ({ ...point, lat: updated[index].lat, lng: updated[index].lng }));
        });
    }, []);

    const exportRoute = () => {
        if (positions.length < 2) return;
        const coordinate = (point: { lat: number; lng: number }) => `${point.lat},${point.lng}`;
        const origin = encodeURIComponent(coordinate(positions[0]));
        const destination = encodeURIComponent(coordinate(positions[positions.length - 1]));
        const waypoints = positions.slice(1, -1).map(coordinate).join("|");
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ""}&travelmode=driving`;
        window.open(`https://wa.me/?text=${encodeURIComponent(`Veja a rota: ${mapsUrl}`)}`, "_blank", "noopener,noreferrer");
    };

    return (
        <Box className="tw-h-full tw-min-h-0 tw-min-w-0 tw-overflow-y-auto tw-py-2 tw-font-prometo">
            <Toast ref={toastRef} position="center" />
            <Box className="tw-grid tw-min-h-0 tw-min-w-0 tw-grid-cols-1 tw-gap-3 lg:tw-h-full lg:tw-grid-cols-[minmax(280px,31%)_minmax(0,1fr)] lg:tw-grid-rows-[minmax(0,1fr)]">
                <Paper elevation={0} className="report-map-side-panel tw-flex tw-min-h-0 tw-min-w-0 tw-flex-col tw-gap-3 tw-overflow-hidden tw-rounded-xl tw-border-0 tw-bg-gtr-surface-muted tw-p-3 tw-shadow-none sm:tw-p-4" sx={{ height: { xs: "auto", lg: "100%" }, maxHeight: { xs: 440, lg: "none" } }}>
                    <Typography variant="subtitle1" className="!tw-font-prometo !tw-font-bold !tw-text-gtr-text">Planejar trajeto</Typography>
                    <Box className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-y-auto tw-px-1 tw-pb-1 tw-pt-3">
                        {points.map((point, index) => (
                            <Box key={index} className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                                <Autocomplete<Place, false, false, true>
                                    freeSolo loading={loadingIndex === index} options={point.options}
                                    getOptionLabel={(option) => typeof option === "string" ? option : option.display_name}
                                    inputValue={point.query} filterOptions={(options) => options}
                                    onInputChange={(_, value, reason) => { if (reason === "input") searchAddress(index, value); }}
                                    onChange={(_, value) => { if (value && typeof value !== "string") selectPlace(index, value); }}
                                    sx={{ flex: 1, minWidth: 0 }}
                                    renderInput={(params) => <TextField {...params} fullWidth label={index === 0 ? "Ponto de partida" : index === points.length - 1 ? "Ponto de destino" : `Parada ${index}`} placeholder="Pesquise um endereço" InputLabelProps={{ shrink: true }} sx={routeFieldSx} />}
                                />
                                {index > 0 && index < points.length - 1 && <Tooltip title="Remover parada" arrow><IconButton size="small" aria-label="Remover parada" onClick={() => setPoints((current) => current.filter((_, position) => position !== index))} className="!tw-h-10 !tw-w-10 !tw-flex-none !tw-bg-transparent !tw-text-gtr-teal dark:!tw-text-slate-200"><CloseOutlined fontSize="small" /></IconButton></Tooltip>}
                            </Box>
                        ))}
                        <Button variant="outlined" startIcon={<AddOutlined />} disabled={points.length >= MAX_POINTS} onClick={() => setPoints((current) => current.length >= MAX_POINTS ? current : [...current.slice(0, -1), emptyPoint(), current[current.length - 1]])} className="!tw-self-start !tw-border-gtr-teal !tw-font-prometo !tw-text-gtr-teal dark:!tw-border-slate-400 dark:!tw-text-slate-200" sx={{ textTransform: "none" }}>Adicionar parada</Button>
                    </Box>
                    <Typography variant="caption" className="!tw-font-prometo !tw-text-gtr-text-muted">Selecione os pontos para traçar a rota. Você pode ajustá-la no mapa; nada será salvo no cadastro.</Typography>
                    <Box className="tw-mt-auto tw-flex tw-justify-end tw-border-t tw-border-gtr-border/60 tw-pt-3">
                        <Button variant="contained" startIcon={<SendToMobileOutlined />} onClick={exportRoute} disabled={positions.length < 2} className="!tw-rounded-lg !tw-bg-gtr-green !tw-font-prometo !tw-text-white hover:!tw-bg-gtr-green-hover disabled:!tw-bg-gtr-disabled disabled:!tw-text-gtr-disabled-text" sx={{ textTransform: "none" }}>Exportar rota</Button>
                    </Box>
                </Paper>
                <Paper elevation={0} className="tw-min-h-0 tw-min-w-0 tw-overflow-hidden tw-rounded-xl tw-border-0 tw-bg-gtr-surface-muted tw-shadow-none" sx={{ height: { xs: 400, lg: "100%" } }}>
                    {GTR_MAPS_KEY ? <GPositionsMap mapRef={mapRef} center={positions[0] ?? { lat: -15.5986686, lng: -56.0991301 }} zoom={14} style={{ width: "100%", height: "100%" }}>
                        <Directions positions={positions} onHandleChange={onRouteChanged} />
                    </GPositionsMap> : <Alert severity="warning">Chave do mapa não configurada.</Alert>}
                </Paper>
            </Box>
        </Box>
    );
};

export default RoutesSimpleTab;
